import React, { useEffect, useState } from 'react';
import { Card } from '@abyss/web/ui/Card';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { Layout } from '@abyss/web/ui/Layout';
import { Heading } from '@abyss/web/ui/Heading';
import { Label } from '@abyss/web/ui/Label';
import { Box } from '@abyss/web/ui/Box';
import { PageTitleService } from '@src/api/common/PageTitleService';
import { BreadcrumbService } from '@src/api/common/BreadcrumbService';
import { BreadcrumbItem } from '@src/api/common/BreadcrumbTypes';
import { PageTitle } from '@src/api/common/PageTitleType';
import { CookieService } from '@src/api/common/CookieService';
import { API_URL } from '@src/constants/BaseUrls';

const MonitorTitle: PageTitle = { title: '' };
const MonitorBreadcrumbs: Array<BreadcrumbItem> = [
  { title: 'Home', isLink: true, href: '/' },
  { title: 'Route Tool', isLink: false },
  { title: 'Monitor Upload', isLink: false },
];

const UPLOAD_PROCESSING_KEY = 'routeToolUploadProcessing';

const normalizeBaseUrl = (baseUrl?: string) => {
  if (!baseUrl) return '';
  return baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
};

const resolveApiBase = () => {
  const configured = normalizeBaseUrl(API_URL);
  if (!configured || configured.includes('xyz-dev.com')) return 'http://localhost:5000';
  return configured;
};

const buildApiUrl = (base: string, path: string) => {
  if (base.endsWith('/api') && path.startsWith('/api/')) return `${base}${path.slice(4)}`;
  return `${base}${path}`;
};

export const MonitorScreen = () => {
  const baseUrl = resolveApiBase();
  const { UpdatePageTitle } = PageTitleService();
  const { UpdateBreadcrumb } = BreadcrumbService();

  const getAuthHeaders = (): Record<string, string> => {
    const token = CookieService.GetCookie('authToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const [uploadProcessing, setUploadProcessing] = useState<{
    entity: string;
    startedAt: number;
  } | null>(null);
  const [uploadProcessingMsg, setUploadProcessingMsg] = useState('');
  const [uploadBarProgress, setUploadBarProgress] = useState(5);
  const [completed, setCompleted] = useState(false);

  // Set page title + breadcrumb
  useEffect(() => {
    (async () => {
      await UpdatePageTitle(MonitorTitle);
      await UpdateBreadcrumb(MonitorBreadcrumbs);
    })();
  }, []);

  // Read sessionStorage on mount
  useEffect(() => {
    try {
      const raw = window.sessionStorage.getItem(UPLOAD_PROCESSING_KEY);
      if (raw) {
        const state = JSON.parse(raw) as { entity: string; startedAt: number };
        if (state?.entity) {
          setUploadProcessing(state);
          setUploadProcessingMsg('Processing uploaded files…');
          setCompleted(false);
        }
      }
    } catch {
      // ignore
    }
  }, []);

  // Listen for custom event dispatched by UploadScreen
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ entity: string; startedAt: number }>).detail;
      if (detail?.entity) {
        setUploadProcessing({ entity: detail.entity, startedAt: detail.startedAt });
        setUploadProcessingMsg('Processing uploaded files…');
        setUploadBarProgress(5);
        setCompleted(false);
      }
    };
    window.addEventListener('routeToolUploadStarted', handler);
    return () => window.removeEventListener('routeToolUploadStarted', handler);
  }, []);

  // Fake progress animation
  useEffect(() => {
    if (!uploadProcessing) return undefined;
    setUploadBarProgress(5);
    const interval = setInterval(() => {
      setUploadBarProgress((prev) => {
        const next = prev + Math.random() * 6 + 2;
        return next >= 90 ? 90 : next;
      });
    }, 700);
    return () => clearInterval(interval);
  }, [uploadProcessing]);

  // Poll backend status
  useEffect(() => {
    if (!uploadProcessing) return undefined;
    let cancelled = false;
    const { entity, startedAt } = uploadProcessing;
    const statusUrl = buildApiUrl(
      baseUrl,
      `/api/entities/${encodeURIComponent(entity)}/status`
    );

    const poll = async () => {
      while (!cancelled) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const response = await fetch(statusUrl, { headers: getAuthHeaders() });
          if (response.ok) {
            // eslint-disable-next-line no-await-in-loop
            const body = await response.json().catch(() => ({}));
            const blobs = body?.blobs ?? {};
            const allComplete =
              blobs.all_complete === true ||
              (blobs.site_clusters === true &&
                blobs.site_travel === true &&
                blobs.user_sites === true &&
                blobs.warehouse_clusters === true);
            const stage: string = body?.job?.stage || body?.stage || '';
            const elapsed = Math.round((Date.now() - startedAt) / 1000);
            const stageLabel = stage ? ` — ${stage}` : '';
            setUploadProcessingMsg(`Processing${stageLabel} (${elapsed}s)…`);
            if (allComplete) {
              setUploadBarProgress(100);
              setTimeout(() => {
                try {
                  window.sessionStorage.removeItem(UPLOAD_PROCESSING_KEY);
                } catch { /* */ }
                setUploadProcessing(null);
                setUploadProcessingMsg('');
                setCompleted(true);
              }, 1200);
              return;
            }
          }
        } catch {
          // network error — keep polling
        }
        // eslint-disable-next-line no-await-in-loop
        await new Promise<void>((resolve) => { setTimeout(resolve, 3000); });
      }
    };

    void poll();
    return () => { cancelled = true; };
  }, [uploadProcessing]);

  return (
    <Box
      padding={24}
      css={{
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        minHeight: 'calc(100vh - 320px)',
      }}
    >
      <Card>
        <Card.Section>
          {/* Header row */}
          <Layout.Group alignItems="center" space={10} css={{ marginBottom: '20px' }}>
            <IconSymbol icon="upload" size={24} color="$primary1" variant="filled" />
            <Heading display="h4" color="$primary1">
              Monitor Upload
            </Heading>
            {uploadProcessing && (
              <div
                style={{
                  marginLeft: 'auto',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <div
                  style={{
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    backgroundColor: '#196ecf',
                    animation: 'monitorPulse 1.4s ease-in-out infinite',
                  }}
                />
                <span style={{ fontSize: '12px', color: '#196ecf', fontWeight: 600 }}>
                  Processing
                </span>
              </div>
            )}
          </Layout.Group>

          {/* Active upload */}
          {uploadProcessing ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: '8px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <IconSymbol icon="corporate_fare" size={18} color="$primary1" />
                  <span style={{ fontWeight: 700, fontSize: '15px', color: '#1a2d4a' }}>
                    {uploadProcessing.entity}
                  </span>
                </div>
                <span style={{ fontSize: '12px', color: '#6b7a8d' }}>
                  {uploadProcessingMsg}
                </span>
              </div>

              {/* Linear progress bar */}
              <div>
                <div
                  style={{
                    width: '100%',
                    height: '8px',
                    backgroundColor: '#e8edf4',
                    borderRadius: '4px',
                    overflow: 'hidden',
                    position: 'relative',
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      height: '100%',
                      width: `${uploadBarProgress}%`,
                      backgroundColor: '#196ecf',
                      borderRadius: '4px',
                      transition: 'width 0.5s ease',
                    }}
                  />
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginTop: '6px',
                  }}
                >
                  <Label size="sm" color="$gray6">
                    Backend is processing your uploaded files
                  </Label>
                  <span style={{ fontSize: '12px', color: '#196ecf', fontWeight: 700 }}>
                    {Math.round(uploadBarProgress)}%
                  </span>
                </div>
              </div>

              <Label size="sm" color="$gray5">
                This page will update automatically. You can navigate away — progress is tracked in the background.
              </Label>
            </div>
          ) : completed ? (
            /* Completed state */
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '16px',
                borderRadius: '8px',
                backgroundColor: '#f0faf4',
                border: '1px solid #b3e0c3',
              }}
            >
              <IconSymbol icon="check_circle" size={28} color="$success2" variant="filled" />
              <div>
                <div style={{ fontWeight: 700, fontSize: '14px', color: '#1a5c35' }}>
                  Upload processing complete!
                </div>
                <Label size="sm" color="$gray6">
                  All files have been processed successfully. Go to Search to view results.
                </Label>
              </div>
            </div>
          ) : (
            /* Idle state */
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '20px 0',
                color: '#9aa5b5',
              }}
            >
              <IconSymbol icon="inbox" size={32} color="$gray4" />
              <div>
                <div style={{ fontWeight: 600, fontSize: '14px', color: '#6b7a8d' }}>
                  No active uploads
                </div>
                <Label size="sm" color="$gray5">
                  Upload a file from the Upload page. Progress will appear here automatically.
                </Label>
              </div>
            </div>
          )}
        </Card.Section>
        <style>{`
          @keyframes monitorPulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50%       { opacity: 0.4; transform: scale(0.8); }
          }
        `}</style>
      </Card>
    </Box>
  );
};
